import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { authActions } from '@libs/store';
import { localStorageKey } from '@libs/utils';
import {
  ASSISTANT_CHAT_WINDOW_DEFAULT_HEIGHT,
  ASSISTANT_CHAT_WINDOW_DEFAULT_WIDTH,
  ASSISTANT_CHAT_WINDOW_MIN_HEIGHT,
  ASSISTANT_CHAT_WINDOW_MIN_WIDTH,
} from './assistant-chat-window.constants';

export interface AssistantChatWindowGeometry {
  width: number;
  height: number;
  // Free drag offset from the window's CSS anchor (bottom-left corner of the layout)
  translateX: number;
  translateY: number;
}

const defaultGeometry: AssistantChatWindowGeometry = {
  width: ASSISTANT_CHAT_WINDOW_DEFAULT_WIDTH,
  height: ASSISTANT_CHAT_WINDOW_DEFAULT_HEIGHT,
  translateX: 0,
  translateY: 0,
};

// Stable open/close contract for the assistant chat window. The window's own content is owned by
// FW10-4045/FW10-4046 - this ticket only flips the signal so those tickets have something to build against.
@Injectable({ providedIn: 'root' })
export class AssistantChatWindowService {
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);

  private readonly isOpenSignal = signal(false);
  public readonly isOpen = this.isOpenSignal.asReadonly();

  // A resized/moved window keeps its size and place for the user session: it is stored so it survives a page
  // reload (the user stays logged in), and dropped on logout so the next user starts from the default.
  private readonly geometrySignal = signal<AssistantChatWindowGeometry>(this.readStoredGeometry());
  public readonly geometry = this.geometrySignal.asReadonly();

  constructor() {
    // This is a root singleton, so it survives logout/login - the reset has to be explicit.
    this.actions$.pipe(ofType(authActions.onLogout), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      localStorage.removeItem(localStorageKey.assistantChatWindowGeometry);
      this.geometrySignal.set(defaultGeometry);
    });
  }

  public open(): void {
    this.isOpenSignal.set(true);
  }

  public close(): void {
    this.isOpenSignal.set(false);
  }

  public toggle(): void {
    this.isOpenSignal.update((value) => !value);
  }

  public setGeometry(geometry: AssistantChatWindowGeometry): void {
    this.geometrySignal.set(geometry);
    localStorage.setItem(localStorageKey.assistantChatWindowGeometry, JSON.stringify(geometry));
  }

  private readStoredGeometry(): AssistantChatWindowGeometry {
    try {
      const stored = JSON.parse(
        localStorage.getItem(localStorageKey.assistantChatWindowGeometry) ?? 'null',
      ) as Partial<AssistantChatWindowGeometry> | null;
      const { width, height, translateX, translateY } = stored ?? {};

      if ([width, height, translateX, translateY].every((value) => Number.isFinite(value))) {
        return {
          width: Math.max(ASSISTANT_CHAT_WINDOW_MIN_WIDTH, width as number),
          height: Math.max(ASSISTANT_CHAT_WINDOW_MIN_HEIGHT, height as number),
          translateX: translateX as number,
          translateY: translateY as number,
        };
      }
    } catch {
      // Corrupt entry - fall back to the default
    }

    return defaultGeometry;
  }
}
